/**
 * iter122 — Guest Identity Document capture / upload modal.
 *
 * Was Aadhaar-only in iter106. Now supports five identity document types:
 *   • Aadhaar Card
 *   • PAN Card
 *   • Passport
 *   • Driving License
 *   • Voter ID Card
 *
 * File name is preserved (`AadhaarCaptureModal.js`) so all existing imports in
 * CalendarScreen / voucher flows keep working without breaking backward
 * compatibility. `document_type` defaults to AADHAAR so any caller that
 * doesn't pass a type gets the historic behaviour.
 *
 * Camera capture (<input capture="environment">) and gallery upload both
 * continue to work exactly as before — only the header, copy, and the type
 * selector chip row are new.
 */
import React, { useEffect, useRef, useState } from 'react';
import { X, Camera, Upload, Loader2, AlertTriangle, CheckCircle2, FileImage } from 'lucide-react';
import { guestDocumentAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import {
  acquireImageNative,
  isNativeCapacitor,
  makeFakeFileEvent,
} from '../utils/nativeImagePicker';

const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

// The 5 identity document types supported by the backend allow-list.
export const DOCUMENT_TYPES = [
  { code: 'AADHAAR',         label: 'Aadhaar Card' },
  { code: 'PAN',             label: 'PAN Card' },
  { code: 'PASSPORT',        label: 'Passport' },
  { code: 'DRIVING_LICENSE', label: 'Driving License' },
  { code: 'VOTER_ID',        label: 'Voter ID Card' },
];

// Same low-memory image compressor as iter106 — Android cameras produce
// 12-24MP images that OOM the browser if decoded at full resolution.
async function compressImage(file, { maxLongEdge = 1600, quality = 0.8 } = {}) {
  if (!file.type.startsWith('image/')) return file;
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, {
        resizeWidth: maxLongEdge, resizeHeight: maxLongEdge, resizeQuality: 'medium',
      });
      let { width, height } = bitmap;
      const longEdge = Math.max(width, height);
      if (longEdge > maxLongEdge) {
        const s = maxLongEdge / longEdge;
        width = Math.round(width * s); height = Math.round(height * s);
      }
      const canvas = (typeof OffscreenCanvas === 'function')
        ? new OffscreenCanvas(width, height)
        : Object.assign(document.createElement('canvas'), { width, height });
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, width, height);
      ctx.drawImage(bitmap, 0, 0, width, height);
      try { bitmap.close && bitmap.close(); } catch (_) { /* ignore */ }
      const blob = (typeof canvas.convertToBlob === 'function')
        ? await canvas.convertToBlob({ type: 'image/jpeg', quality })
        : await new Promise((res) => canvas.toBlob(res, 'image/jpeg', quality));
      if (!blob) throw new Error('toBlob returned null');
      return new File(
        [blob],
        (file.name || 'guest-doc').replace(/\.[^.]+$/, '') + '.jpg',
        { type: 'image/jpeg', lastModified: Date.now() },
      );
    } catch (err) {
      console.warn('[GuestDoc] createImageBitmap path failed:', err && err.message);
    }
  }
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      const cleanup = () => { try { URL.revokeObjectURL(url); } catch (_) { /* ignore */ } };
      img.onload = () => {
        try {
          let { width, height } = img;
          const longEdge = Math.max(width, height);
          if (longEdge > maxLongEdge) {
            const s = maxLongEdge / longEdge;
            width = Math.round(width * s); height = Math.round(height * s);
          }
          const canvas = document.createElement('canvas');
          canvas.width = width; canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
          img.src = '';
          canvas.toBlob(
            (blob) => {
              cleanup();
              if (!blob) return reject(new Error('Compression failed'));
              resolve(new File(
                [blob],
                (file.name || 'guest-doc').replace(/\.[^.]+$/, '') + '.jpg',
                { type: 'image/jpeg', lastModified: Date.now() },
              ));
            },
            'image/jpeg', quality,
          );
        } catch (e) { cleanup(); reject(e); }
      };
      img.onerror = (e) => { cleanup(); reject(e); };
      img.decoding = 'async';
      img.src = url;
    });
  } catch (err) {
    console.warn('[GuestDoc] Legacy compression failed, sending raw file:', err && err.message);
    return file;
  }
}

const AadhaarCaptureModal = ({ booking, onClose, onUploaded, initialDocumentType = 'AADHAAR' }) => {
  const [documentType, setDocumentType] = useState(initialDocumentType);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [error, setError] = useState(null);
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const selectedLabel = DOCUMENT_TYPES.find((d) => d.code === documentType)?.label || 'Identity Document';

  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  useEffect(() => {
    const release = () => setPickerOpen(false);
    window.addEventListener('focus', release);
    return () => window.removeEventListener('focus', release);
  }, []);

  const openCamera = async () => {
    if (pickerOpen || uploading || processing) return;
    setError(null);
    // iter136 — On Capacitor Android/iOS, replace the HTML capture input
    // (which mis-fires inside the WebView) with the native Camera plugin.
    // Web path is untouched: falls back to the HTML file input below.
    if (isNativeCapacitor()) {
      setPickerOpen(true);
      try {
        const file = await acquireImageNative({ source: 'camera' });
        // Synthesise an event-shaped object so onFilePicked stays unchanged.
        await onFilePicked(makeFakeFileEvent(file));
      } catch (err) {
        console.error('[camera] native capture failed', err);
        setError(err?.message || 'Could not open the camera. Please try again.');
      } finally {
        setPickerOpen(false);
      }
      return;
    }
    setPickerOpen(true);
    if (cameraInputRef.current) cameraInputRef.current.value = '';
    cameraInputRef.current?.click();
  };
  const openFilePicker = async () => {
    if (pickerOpen || uploading || processing) return;
    setError(null);
    // iter146 — Gallery selection always goes through the HTML file
    // input. Inside the Capacitor Android WebView (Chromium 111+) this
    // is delegated to the OS Photo Picker (PickVisualMedia) on Android
    // 13+, so we can drop READ_MEDIA_IMAGES and remain Google-Play
    // policy compliant. On older Android/iOS/web the standard input
    // opens a scoped picker that returns a content URI — no media
    // permission required either way.
    setPickerOpen(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
    fileInputRef.current?.click();
  };

  const onFilePicked = async (e) => {
    setError(null); setPickerOpen(false);
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED.includes(f.type)) {
      setError('Only JPG, JPEG, PNG, or WEBP files are allowed.'); return;
    }
    if (f.size > MAX_BYTES) {
      setError('Maximum file size is 10MB.'); return;
    }
    setProcessing(true);
    try {
      const compressed = await compressImage(f);
      setSelectedFile(compressed);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(compressed));
    } catch (err) {
      console.error(err);
      setError('Could not process the image on this device. Try a smaller photo.');
    } finally {
      setProcessing(false);
    }
  };

  const handleReplace = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setError(null);
  };

  const handleUpload = async () => {
    if (!selectedFile || uploading) return;
    setUploading(true); setError(null);
    try {
      const res = await guestDocumentAPI.upload(booking.booking_id, selectedFile, documentType);
      const data = res?.data?.RESULT || res?.RESULT;
      toast({ title: `${selectedLabel} saved`, description: 'Document uploaded successfully.' });
      onUploaded?.(data);
      onClose?.();
    } catch (err) {
      // iter135 — detail is now an object: { message, detected_document_type, confidence, validation_reason }
      const detail = err?.response?.data?.detail;
      let msg = 'Upload failed.';
      if (detail && typeof detail === 'object') {
        msg = detail.message || msg;
        if (detail.detected_document_type && detail.detected_document_type !== 'UNKNOWN'
            && detail.detected_document_type !== documentType) {
          const detectedLabel = DOCUMENT_TYPES.find((d) => d.code === detail.detected_document_type)?.label
            || detail.detected_document_type;
          msg = `${msg} (Detected: ${detectedLabel})`;
        }
      } else if (typeof detail === 'string') {
        msg = detail;
      } else if (err?._friendlyMessage) {
        msg = err._friendlyMessage;
      } else if (err?.message) {
        msg = err.message;
      }
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-2 sm:p-4"
      data-testid="aadhaar-capture-modal"
      onClick={() => !uploading && onClose?.()}
    >
      <div
        className="bg-white rounded-2xl w-full sm:max-w-md max-h-[92vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900" data-testid="guest-doc-modal-title">
              Upload Guest Identity Document
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">Booking {booking.booking_id}</p>
          </div>
          <button
            onClick={() => !uploading && onClose?.()}
            className="p-1.5 rounded-lg hover:bg-slate-100"
            data-testid="aadhaar-modal-close"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* iter122 — Document type selector. 5 chips, single-select. */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-2">
              Document Type
            </label>
            <div className="flex flex-wrap gap-1.5" data-testid="guest-doc-type-selector">
              {DOCUMENT_TYPES.map((d) => {
                const active = documentType === d.code;
                return (
                  <button
                    key={d.code}
                    type="button"
                    onClick={() => setDocumentType(d.code)}
                    disabled={processing || uploading}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors disabled:opacity-60 ${
                      active
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-400'
                    }`}
                    data-testid={`guest-doc-type-${d.code}`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>

          {!selectedFile && (
            <>
              <p className="text-sm text-slate-600">
                Capture or upload the guest&apos;s <span className="font-medium">{selectedLabel}</span>.
                {documentType !== 'VOTER_ID' && ' We use AI to verify the document type before saving.'}
              </p>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={openCamera}
                  disabled={pickerOpen || processing || uploading}
                  className="flex flex-col items-center justify-center gap-2 p-5 rounded-xl border-2 border-dashed border-emerald-200 bg-emerald-50/40 hover:bg-emerald-50 transition disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="aadhaar-capture-camera-btn"
                >
                  <Camera className="w-8 h-8 text-emerald-600" />
                  <span className="text-sm font-semibold text-emerald-700">Capture Photo</span>
                  <span className="text-[11px] text-slate-500">Use rear camera</span>
                </button>
                <button
                  onClick={openFilePicker}
                  disabled={pickerOpen || processing || uploading}
                  className="flex flex-col items-center justify-center gap-2 p-5 rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/40 hover:bg-blue-50 transition disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="aadhaar-upload-file-btn"
                >
                  <Upload className="w-8 h-8 text-blue-600" />
                  <span className="text-sm font-semibold text-blue-700">Upload from Gallery</span>
                  <span className="text-[11px] text-slate-500">JPG / PNG up to 10MB</span>
                </button>
              </div>

              {processing && (
                <div className="flex items-center justify-center gap-2 text-sm text-slate-600 py-2" data-testid="aadhaar-processing">
                  <Loader2 className="w-4 h-4 animate-spin" /> Processing image…
                </div>
              )}

              <p className="text-[11px] text-slate-500 leading-relaxed">
                <FileImage className="w-3 h-3 inline mr-1" />
                {documentType === 'VOTER_ID'
                  ? 'The selected document type will be stored alongside the file. Ensure the image is clear and readable.'
                  : 'AI validation runs on the server after upload. Any mismatched document (wrong type or unrelated image) will be rejected automatically.'}
              </p>
            </>
          )}

          {selectedFile && previewUrl && (
            <>
              <div className="rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-full max-h-[40vh] object-contain bg-slate-100"
                  data-testid="aadhaar-preview-img"
                />
              </div>
              <div className="text-xs text-slate-500 flex items-center justify-between">
                <span className="truncate">{selectedFile.name}</span>
                <span>{(selectedFile.size / 1024).toFixed(0)} KB · {selectedLabel}</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleReplace}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-slate-50"
                  data-testid="aadhaar-retry-btn"
                  disabled={uploading}
                >
                  Replace
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-60 flex items-center justify-center gap-2"
                  data-testid="aadhaar-confirm-upload-btn"
                >
                  {uploading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
                  ) : (
                    <><CheckCircle2 className="w-4 h-4" /> Save {selectedLabel}</>
                  )}
                </button>
              </div>
            </>
          )}

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm" data-testid="aadhaar-error">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <input
            ref={cameraInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            onChange={onFilePicked}
            className="hidden"
            data-testid="aadhaar-camera-input"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={onFilePicked}
            className="hidden"
            data-testid="aadhaar-file-input"
          />
        </div>
      </div>
    </div>
  );
};

export default AadhaarCaptureModal;
